// ============================================================================
// CrewAhead Maritime Platform - Notifications API Functions
// ============================================================================
// Notifications: list, retrieve, mark read (single/batch/all), unread count,
// clear all, preferences, and admin create/bulk create.

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NotificationItem {
  id: string;
  notificationType: string;
  title: string;
  message: string;
  isRead: boolean;
  dataPayload: Record<string, unknown> | null;
  channels: string[];
  sender: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
  createdAt: string;
  readAt: string | null;
  targetType: string | null;
  targetId: string | null;
}

export interface NotificationPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  inAppNotifications: boolean;
  smsNotifications: boolean;
  paymentAlerts: boolean;
  jobMatches: boolean;
  applicationUpdates: boolean;
  messages: boolean;
  subscriptionReminders: boolean;
  marketing: boolean;
  profileAlerts: boolean;
  welcomeNotifications: boolean;
}

export interface UnreadCountResponse {
  unreadCount: number;
  byType: Record<string, number>;
}

export interface AdminCreateNotificationInput {
  userId: string;
  notificationType: string;
  title: string;
  message: string;
  channels?: string[];
  dataPayload?: Record<string, unknown>;
}

export interface AdminCreateNotificationResponse {
  status: string;
  notificationId?: string;
  message: string;
}

export interface BulkNotificationItem {
  userId: string;
  notificationType: string;
  title: string;
  message: string;
  channels?: string[];
  dataPayload?: Record<string, unknown>;
  skipPreferences?: boolean;
}

export interface BulkCreateNotificationResponse {
  status: string;
  created: string[];
  createdCount: number;
  filteredCount: number;
  errors: Array<{ userId: string; error: string }>;
}

// ─── List ────────────────────────────────────────────────────────────────────

/**
 * GET /notifications/
 *
 * List notifications for the authenticated user.
 * Supports filtering by read status and type, plus ordering.
 *
 * @param params - Optional filters and pagination
 * @returns Paginated list of notifications
 */
export async function getNotifications(params?: {
  isRead?: boolean;
  notificationType?: string;
  ordering?: 'created_at' | '-created_at' | 'is_read' | '-is_read';
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  next: string | null;
  previous: string | null;
  results: NotificationItem[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.isRead !== undefined) query.set('is_read', String(params.isRead));
    if (params?.notificationType) query.set('notification_type', params.notificationType);
    if (params?.ordering) query.set('ordering', params.ordering);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.NOTIFICATIONS.LIST}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      next: string | null;
      previous: string | null;
      results: NotificationItem[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Detail ──────────────────────────────────────────────────────────────────

/**
 * GET /notifications/{notification_id}/
 *
 * Retrieve a single notification. Auto-marks as read if unread.
 *
 * @param id - Notification ID
 * @returns Notification detail
 */
export async function getNotification(id: string): Promise<NotificationItem> {
  try {
    const response = await apiClient.get<NotificationItem>(
      API_ENDPOINTS.NOTIFICATIONS.DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Delete Single ───────────────────────────────────────────────────────────

/**
 * DELETE /notifications/{notification_id}/
 *
 * Delete (dismiss) a single notification.
 *
 * @param id - Notification ID
 * @returns Confirmation response
 */
export async function deleteNotification(
  id: string
): Promise<{
  status: string;
  message: string;
  deletedCount: number;
}> {
  try {
    const response = await apiClient.delete<{
      status: string;
      message: string;
      deleted_count: number;
    }>(API_ENDPOINTS.NOTIFICATIONS.DETAIL(id));
    return {
      status: response.data.status,
      message: response.data.message,
      deletedCount: response.data.deleted_count,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Mark Single Read ────────────────────────────────────────────────────────

/**
 * POST /notifications/{notification_id}/mark_read
 *
 * Mark a single notification as read.
 *
 * @param id - Notification ID
 * @returns Confirmation response
 */
export async function markNotificationRead(
  id: string
): Promise<{
  status: string;
  message: string;
  notificationId: string;
}> {
  try {
    const response = await apiClient.post<{
      status: string;
      message: string;
      notification_id: string;
    }>(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(id));
    return {
      status: response.data.status,
      message: response.data.message,
      notificationId: response.data.notification_id,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Mark All Read ───────────────────────────────────────────────────────────

/**
 * POST /notifications/mark_all_read/
 *
 * Mark all (or specified) notifications as read.
 *
 * @param notificationIds - Optional list of notification IDs to mark as read.
 *                          If omitted, all notifications are marked read.
 * @returns Confirmation with count of marked notifications
 */
export async function markAllNotificationsRead(
  notificationIds?: string[]
): Promise<{
  status: string;
  message: string;
  markedCount: number;
}> {
  try {
    const response = await apiClient.post<{
      status: string;
      message: string;
      marked_count: number;
    }>(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ, {
      notification_ids: notificationIds,
    });
    return {
      status: response.data.status,
      message: response.data.message,
      markedCount: response.data.marked_count,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Unread Count ────────────────────────────────────────────────────────────

/**
 * GET /notifications/unread_count/
 *
 * Get unread notification count with type breakdown.
 *
 * @returns Unread count and type breakdown
 */
export async function getUnreadNotificationCount(): Promise<UnreadCountResponse> {
  try {
    const response = await apiClient.get<UnreadCountResponse>(
      API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Unread List ─────────────────────────────────────────────────────────────

/**
 * GET /notifications/unread/
 *
 * Return unread notifications count and list for the current user.
 *
 * @returns Unread count and recent notifications
 */
export async function getUnreadNotifications(): Promise<{
  count: number;
  notifications: NotificationItem[];
}> {
  try {
    const response = await apiClient.get<{
      count: number;
      notifications: NotificationItem[];
    }>(API_ENDPOINTS.NOTIFICATIONS.UNREAD_LIST);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Clear All ───────────────────────────────────────────────────────────────

/**
 * DELETE /notifications/clear_all/
 *
 * Delete all notifications for the current user.
 * Optionally filter by read status.
 *
 * @param isRead - If provided, only delete notifications with matching read status
 * @returns Confirmation with count of deleted notifications
 */
export async function clearAllNotifications(
  isRead?: boolean
): Promise<{
  status: string;
  message: string;
  deletedCount: number;
}> {
  try {
    const query = new URLSearchParams();
    if (isRead !== undefined) query.set('is_read', String(isRead));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.NOTIFICATIONS.CLEAR_ALL}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.delete<{
      status: string;
      message: string;
      deleted_count: number;
    }>(url);
    return {
      status: response.data.status,
      message: response.data.message,
      deletedCount: response.data.deleted_count,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Preferences ─────────────────────────────────────────────────────────────

/**
 * GET /notifications/preferences/
 *
 * Get notification preferences (DB-backed with cache).
 *
 * @returns Current notification preferences
 */
export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  try {
    const response = await apiClient.get<NotificationPreferences>(
      API_ENDPOINTS.NOTIFICATIONS.PREFERENCES
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PUT /notifications/preferences/
 *
 * Update notification preferences (DB-backed with cache).
 *
 * @param preferences - Updated preference settings
 * @returns Updated preferences confirmation
 */
export async function updateNotificationPreferences(
  preferences: Partial<NotificationPreferences>
): Promise<{
  status: string;
  message: string;
  preferences: NotificationPreferences;
}> {
  try {
    const response = await apiClient.put<{
      status: string;
      message: string;
      preferences: NotificationPreferences;
    }>(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES, preferences);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Admin: Create ───────────────────────────────────────────────────────────

/**
 * POST /notifications/admin_create/
 *
 * Admin endpoint to manually create and send a notification.
 *
 * @param data - Notification creation data
 * @returns Creation confirmation
 */
export async function adminCreateNotification(
  data: AdminCreateNotificationInput
): Promise<AdminCreateNotificationResponse> {
  try {
    const response = await apiClient.post<AdminCreateNotificationResponse>(
      API_ENDPOINTS.NOTIFICATIONS.ADMIN_CREATE,
      {
        user_id: data.userId,
        notification_type: data.notificationType,
        title: data.title,
        message: data.message,
        channels: data.channels ?? ['in_app'],
        data_payload: data.dataPayload ?? {},
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Admin: Bulk Create ──────────────────────────────────────────────────────

/**
 * POST /notifications/bulk_create/
 *
 * Batch create notifications (max 500 per request).
 *
 * @param notifications - Array of notification items to create
 * @returns Bulk creation results with created IDs and any errors
 */
export async function adminBulkCreateNotifications(
  notifications: BulkNotificationItem[]
): Promise<BulkCreateNotificationResponse> {
  try {
    const response = await apiClient.post<BulkCreateNotificationResponse>(
      API_ENDPOINTS.NOTIFICATIONS.BULK_CREATE,
      { notifications }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
